import axios from 'axios'
import store from '../store'
import MAAYOT_API, { headers } from '../../utils/config'
import {
  ACCOUNT_SET_LEVEL_DONE,
  ACCOUNT_SET_FIRST_NAME_DONE,
  ACCOUNT_SET_EMAIL_DONE,
  ACCOUNT_SET_PASSWORD_DONE,
  ACCOUNT_UPDATE_ERRORS,
  SET_PROFILE,
  ACCOUNT_SET_CHARACTER_DONE,
} from '../types'
import axiosInstance from '../../axios/axiosInstance'
import { apiErrorText } from '../../i18n/apiError'
import { DownGradePlanProps, StripeCheckoutProps } from 'types/stripeTypes'
import { profileLanguageFields } from '../../i18n/languages'
import { syncLanguageWithProfile } from '../../i18n/profileLanguage'

const clearLocal = () => {
  localStorage.removeItem('persist:storymaayot')
  localStorage.removeItem('persist:quizmaayot')
  localStorage.removeItem('persist:questionmaayot')
  localStorage.removeItem('persist:streakmaayot')
}

// get user details api call
export const getProfile = async () => {
  try {
    const response = await axiosInstance.get(`profile`)
    const data = response.data

    if (response.status === 200 && data) {
      const credentials = {
        memberId: data?.id,
        email: data?.email,
        createdAt: data?.createdAt || null,
        metaData: {
          firstName: data?.customFields?.firstName || '',
        },
        customFields: {
          level: data?.customFields?.level || 'beginner',
          ...(data?.customFields || {}),
          ...profileLanguageFields(data?.customFields),
        },
      }
      const membership = {
        id: data?.id,
        name: data?.membership?.name,
        amount: 0,
        oneTime: false,
        plan: data?.membership?.name,
      }

      const profilePayload = {
        credentials: credentials,
        membership: membership,
        stripe: data?.stripe,
        paymentProfile: data?.paymentProfile,
        trialEligibility: data?.trialEligibility || null,
        backToSchool2026: data?.backToSchool2026 || null,
        subscriptionStatus: data?.subscriptionStatus || null,
      }

      store.dispatch({
        type: SET_PROFILE,
        payload: profilePayload,
      })
      syncLanguageWithProfile(credentials)

      // Update localStorage with fresh profile data
      const existingUserData = localStorage.getItem('user')
      if (existingUserData) {
        try {
          const parsedUserData = JSON.parse(existingUserData)
          const updatedUserData = {
            ...parsedUserData,
            credentials: credentials,
            membership: membership,
            stripe: data?.stripe,
            paymentProfile: data?.paymentProfile,
            trialEligibility: data?.trialEligibility || parsedUserData.trialEligibility || null,
            backToSchool2026: data?.backToSchool2026 || parsedUserData.backToSchool2026 || null,
            subscriptionStatus: data?.subscriptionStatus || null,
          }
          localStorage.setItem('user', JSON.stringify(updatedUserData))
        } catch (error) {}
      }

      return data
    } else {
      store.dispatch({
        type: ACCOUNT_UPDATE_ERRORS,
        payload: 'Error',
      })
      throw new Error('Error')
    }
  } catch (error) {
    store.dispatch({
      type: ACCOUNT_UPDATE_ERRORS,
      payload: 'Error',
    })
    throw error
  }
}
// get user details api call ends

// profile pages api calls

export const SaveCharacter = async (payload: { UpdateCharacter: string }) => {
  try {
    const response = await axiosInstance.put(`profile/custom/character`, payload)
    if (response?.data && response?.status === 200) {
      const character = response?.data?.character || ''
      store.dispatch({
        type: ACCOUNT_SET_CHARACTER_DONE,
        payload: character,
      })
    } else {
      const err = apiErrorText(response)
      store.dispatch({
        type: ACCOUNT_UPDATE_ERRORS,
        payload: err,
      })
    }
    return response?.data
  } catch (error) {
    return error
  }
}

export const saveFirstName = async (data: { firstName: string }) => {
  try {
    const res = await axiosInstance.put(`profile/custom/first-name`, data)
    if (res?.data) {
      store.dispatch({
        type: ACCOUNT_SET_FIRST_NAME_DONE,
        payload: res?.data?.name,
      })
    }
    return res?.data
  } catch (err: any) {
    return apiErrorText(err)
  }
}

export const saveEmail = async ({ email }: { email: string }) => {
  try {
    const res = await axiosInstance.put(`profile/custom/email`, { email })

    if (res?.data?.email) {
      store.dispatch({
        type: ACCOUNT_SET_EMAIL_DONE,
        payload: res?.data?.email,
      })
    }
    return res?.data
  } catch (err: any) {
    return apiErrorText(err)
  }
}

// profile pages api calls ends

// account stripe api calls

export const getStripePlan = async (memberId?: string) => {
  try {
    // ?country=SG on the page URL previews another region's pricing (display only —
    // checkout currency is re-validated server-side against the real visitor country).
    const countryOverride = new URLSearchParams(window.location.search).get('country') || undefined
    const params = {
      ...(memberId ? { memberId } : {}),
      ...(countryOverride ? { country: countryOverride } : {}),
    }
    // `register/stripe-plan` is @Public() on the server, but axiosInstance refuses to send
    // anything without a token — it calls logout() and rejects in its request interceptor.
    // That made every price on a signed-out page unreachable: the request never left the
    // browser, so /offer sat on "Loading prices…" forever and, worse, each load fired
    // posthog.reset() and cleared persisted onboarding state as a side effect of the
    // spurious logout. With no token, ask for the public prices directly.
    if (!localStorage.getItem('authToken')) {
      // baseURL rather than string concatenation so the join matches axiosInstance's,
      // whether or not MAAYOT_API carries a trailing slash.
      const res = await axios.get('register/stripe-plan', { baseURL: MAAYOT_API, headers, params })
      return res?.data
    }
    const res = await axiosInstance.get(`register/stripe-plan`, { params })
    return res?.data
  } catch (error: any) {
    return apiErrorText(error)
  }
}

export const stripeCheckoutLink = async (data: StripeCheckoutProps) => {
  try {
    const res = await axiosInstance.post(`register/stripe-checkout-session`, data)
    return res?.data
  } catch (error: any) {
    return apiErrorText(error)
  }
}

export const stripeBillingPortalLink = async () => {
  try {
    const res = await axiosInstance.get(`register/stripe-billing-portal-session`)
    return res?.data
  } catch (error: any) {
    return apiErrorText(error)
  }
}

export const stripeUpdatePaymentMethodLink = async () => {
  try {
    const res = await axiosInstance.get(`register/stripe-update-payment-method`)
    return res?.data
  } catch (error: any) {
    return apiErrorText(error)
  }
}

export const downGradePlan = async (data: DownGradePlanProps) => {
  try {
    const res = await axiosInstance.post(`register/plan-downgrade-check`, data)
    return res?.data
  } catch (error: any) {
    return apiErrorText(error)
  }
}

// Churn flow (settings → downgrade to Free). All endpoints act on the JWT user's own
// subscription — no ids are sent from the client.

export const churnCancel = async () => {
  const res = await axiosInstance.post(`register/churn-cancel`)
  return res?.data
}

export const churnResume = async () => {
  const res = await axiosInstance.post(`register/churn-resume`)
  return res?.data
}

export const churnPause = async (months: 1 | 2 | 3) => {
  const res = await axiosInstance.post(`register/churn-pause`, { months })
  return res?.data
}

export const churnApplyOffer = async () => {
  const res = await axiosInstance.post(`register/churn-apply-offer`)
  return res?.data
}

export const churnSwitchPlan = async (target: 'standard_monthly' | 'standard_yearly' | 'weekly_yearly') => {
  const res = await axiosInstance.post(`register/churn-switch-plan`, { target })
  return res?.data
}

export const getToastFreeTrial = async () => {
  try {
    const res = await axiosInstance.get(`register/free-plan-trial`)
    return res?.data
  } catch (error: any) {
    return apiErrorText(error)
  }
}

// One-time 24h no-card Standard access offered when a user declines the onboarding trial
export const claimOnboardingDayPass = async () => {
  try {
    const res = await axiosInstance.post(`register/onboarding-day-pass`)
    return res?.data
  } catch (error: any) {
    return apiErrorText(error)
  }
}

// One-time 50%-off-3-months voucher for learners enrolled in the Day 0–5
// re-engagement email sequence (reached via /claim/offer). Returns null on any
// transport error so callers can fall through to the plain plan page.
export const claimDripOffer = async () => {
  try {
    const res = await axiosInstance.post(`register/drip-offer/claim`)
    return res?.data
  } catch (error: any) {
    return null
  }
}

export const downloadInvoice = async () => {
  try {
    const res = await axiosInstance.get(`register/download-invoice`)
    return res?.data
  } catch (error: any) {
    return apiErrorText(error)
  }
}

export const validateCouponCode = async (data: { code: string; priceId: string }) => {
  try {
    const res = await axiosInstance.post(`register/validate-coupon`, data)
    return res?.data
  } catch (error: any) {
    return apiErrorText(error)
  }
}

export const fetchStripeCheckOutSession = async (data: { sessionId: string }) => {
  try {
    const res = await axiosInstance.get(`register/fetch-stripe-checkout-session`, {
      params: { sessionId: data.sessionId },
    })
    return res?.data
  } catch (error: any) {
    return apiErrorText(error)
  }
}

// account stripe api calls ends

export const resetPlacementTest = async ({ language_id }: { language_id?: string }) => {
  try {
    const res = await axiosInstance.patch(`user-language/reset-placement-test`, { language_id })
    return res?.data
  } catch (error: any) {
    return apiErrorText(error)
  }
}
// not in use api calls

export const savePassword =
  (oldPassword: string, newPassword: string, confirmNewPassword: string, email: string) => (dispatch: any) => {
    return new Promise((resolve, reject) => {
      const passwordObj = {
        email,
        oldPassword,
        newPassword,
        confirmNewPassword,
      }

      return fetch(`profile/change-password`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ ...passwordObj }),
      })
        .then((resData) => resData.json())
        .then((data) => {
          if (data && !data.status && !data.statusCode && Object.keys(data) && Object.keys(data).length > 0) {
            dispatch({
              type: ACCOUNT_SET_PASSWORD_DONE,
              payload: data,
            })
            resolve(data)
          } else {
            const err = Array.isArray(data.message) ? data.message[0] : data.message
            dispatch({
              type: ACCOUNT_UPDATE_ERRORS,
              payload: err,
            })
            reject(err)
          }
        })
    })
  }

export const saveLevel = (level: string) => (dispatch: any) => {
  return new Promise((resolve, reject) => {
    const content = {
      level: level?.toLowerCase(),
    }
    return axiosInstance
      .put(`profile/custom/level`, content)
      .then((res) => {
        const data = res?.data
        if (data && !data.status && !data.statusCode && Object.keys(data) && Object.keys(data).length > 0) {
          clearLocal()
          const level = data.member?.customFields?.level || data.member.level || 'Null'
          dispatch({
            type: ACCOUNT_SET_LEVEL_DONE,
            payload: level.toLowerCase(),
          })
          resolve(data)
        } else {
          const err = apiErrorText(data)
          dispatch({
            type: ACCOUNT_UPDATE_ERRORS,
            payload: err,
          })
          reject(err)
        }
      })
      .catch((error) => {
        const err = apiErrorText(error) || 'Error'
        dispatch({
          type: ACCOUNT_UPDATE_ERRORS,
          payload: err,
        })
        reject(err)
      })
  })
}

// ends
